import axios from 'axios';
import { api_labelflow, api_labelflow_training } from "../../../../../config.jsx";

// Fetch summary report
export const handleGetOneAPI = (params) =>
    axios.get(`${api_labelflow}/detection_management/get_one_detection_model_info_by_version_id`, {
        params: params
    });

export const handleDownloadDetectionDataAPI = async (params) => {
  try {
    const response = await axios.post(
      `${api_labelflow_training}/export_management/detection_data_download`,
      new URLSearchParams(params), // Send params as form data
      {
        responseType: 'blob', // Expect binary response
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error downloading classification data:', error);
    throw error;
  }
};

export const startDetectionTrainingAPI = async (data) => {
  try {
    const response = await axios.post(
      `${api_labelflow_training}/training_management/detection/start`,
      new URLSearchParams(data),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error starting detection training:', error);
    throw error;
  }
};

/**
 * AI auto-detection (training API /auto-detect).
 * mode: 'smart'     -> /detect/everything (Roboflow-style: YOLOv8 + YOLO-World +
 *                      FastSAM/CLIP — finds objects even outside every class list)
 *       'myclasses' -> /detect/custom     (YOLO-World restricted to the project's class names)
 *       'coco'      -> /detect/standard   (YOLOv8, 80 COCO classes)
 */
export const autoDetectAPI = async ({
  blob,
  fileName,
  mode,
  confidence,
  iouThreshold,
  classNames,
  signal,
}) => {
  const formData = new FormData();
  formData.append('image', blob, fileName || 'image.jpg');
  formData.append('confidence', confidence);
  if (iouThreshold != null) formData.append('iou_threshold', iouThreshold);

  let endpoint = '/auto-detect/detect/everything';
  if (mode === 'coco') {
    endpoint = '/auto-detect/detect/standard';
  } else if (mode === 'myclasses') {
    endpoint = '/auto-detect/detect/custom';
    formData.append('custom_classes', JSON.stringify(classNames || []));
  } else {
    // project classes get priority in the naming vocabulary
    formData.append('extra_classes', JSON.stringify(classNames || []));
  }

  // `signal` lets AI mode's Stop button abort the in-flight inference request.
  const response = await axios.post(`${api_labelflow_training}${endpoint}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
  });
  return response;
};

/**
 * Master-image guided detection: the boxes the user drew on the master image
 * define both the class set (YOLO-World text prompts) and the visual exemplars
 * (CLIP appearance gate), so only those object types get labeled.
 *
 * masterBoxes: [{ x1, y1, x2, y2, class, probes: [...] }] in MASTER-image pixels.
 * similarity:  0 disables the appearance gate (restrict by class only).
 */
export const autoDetectFromMasterAPI = async ({
  blob,
  fileName,
  masterBlob,
  masterFileName,
  masterBoxes,
  confidence,
  iouThreshold,
  similarity,
  signal,
}) => {
  const formData = new FormData();
  formData.append('image', blob, fileName || 'image.jpg');
  formData.append('master_image', masterBlob, masterFileName || 'master.jpg');
  formData.append('master_boxes', JSON.stringify(masterBoxes || []));
  formData.append('confidence', confidence);
  if (iouThreshold != null) formData.append('iou_threshold', iouThreshold);
  if (similarity != null) formData.append('similarity_threshold', similarity);

  const response = await axios.post(
    `${api_labelflow_training}/auto-detect/detect/from-master`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, signal },
  );
  return response;
};

export const saveDetectionTrainingConfigAPI = async (data) => {
  try {
    const response = await axios.post(
      `${api_labelflow_training}/training_management/save_training_config`,
      new URLSearchParams(data),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error saving detection training config:', error);
    throw error;
  }
};


/**
 * Pre-label an image with one of the project's OWN trained versions.
 *
 * Hits the same `inference_json` endpoint the training-result page uses, so no
 * new backend is involved: it returns
 *   { predictions: [{ classId, className, confidence, bbox: [x1, y1, x2, y2] }] }
 * with class names taken from the trained version, in original-image pixels.
 *
 * `modelDataInfoId` is the trained version's `_id`; `signal` lets Stop abort it.
 */
export const preLabelWithTrainedModelAPI = async ({
  blob,
  fileName,
  modelType,
  modelDataInfoId,
  confidence,
  signal,
}) => {
  const formData = new FormData();
  formData.append('file', blob, fileName || 'image.jpg');

  const query = new URLSearchParams({ modelType, modelDataInfoId });
  if (confidence != null) query.set('conf', String(confidence));

  const response = await axios.post(
    `${api_labelflow_training}/training_management/inference_json?${query.toString()}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, signal },
  );
  return response;
};
